import { Breadcrumb, Button, Grid, Message, Modal } from "semantic-ui-react";
import React, { Component } from "react";
import { matchPath } from "react-router";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import { observer } from "mobx-react";
import { observable } from "mobx";
import ErrorWrapper from "~/components/wrapper/ErrorWrapper";
import Link from "~/components/element/Link";
import ContentLoader from "~/components/element/ContentLoader";
import SubscriptionEditForm from "~/modules/BillingCentral/components/SubscriptionEditForm";
import ToastMessageStore from "~/stores/ToastMessageStore";
import FormStore from "~/stores/FormStore";
import SubscriptionPageStore from "~/stores/Subscription/SubscriptionPageStore";
import LoadingWrapper from "~/components/wrapper/LoadingWrapper";
import RouteWrapper from "~/components/wrapper/RouteWrapper";
import ToastMessageContainer from "~/components/ToastMessageContainer";
import DowngradedSubscriptionItemMessage from "~/modules/BillingCentral/components/DowngradedSubscriptionItemMessage";
import SubscriptionTable from "~/modules/BillingCentral/components/SubscriptionTable";
import SubscriptionCancelAtPeriodEndConfirmationModal from "~/modules/BillingCentral/components/SubscriptionCancelAtPeriodEndConfirmationModal";
import CreditCardFormStore from "~/stores/Credit/CreditCardFormStore";
import ProviderStore from "~/stores/Provider/ProviderStore";
import CustomerStore from "~/stores/CustomerStore";

@observer
class SubscriptionEditPage extends Component {
  @observable subscriptionPageStore = null;

  @observable creditCardFormStore = null;

  constructor(props) {
    super(props);

    const { t } = props;

    document.title = t("billingCentral:uiMeet3dSubscription");

    this.subscriptionPageStore = new SubscriptionPageStore();
    this.subscriptionPageStore.subscriptionStore.isLoading = true;
    this.subscriptionPageStore.subscriptionStore.searchParams.notAsAdminProvider = true;

    this.creditCardFormStore = new CreditCardFormStore();

    this.state = {
      confirmationModalVisible: false,
      cancelAtPeriodEndConfirmationModalVisible: false,
    };
  }

  componentDidMount() {
    const { showError, redirectTo, actualUserStore, location } = this.props;
    const { pathname } = location;
    const { subscriptionStore, otherProductStore, taxStore } = this.subscriptionPageStore;
    const { creditCardStore } = this.creditCardFormStore;

    // React doesn't provide the match params for this case of nested switch
    // We will need to find it by ourselves
    const match = matchPath(pathname, {
      path: "/billing-central/subscriptions/:subscriptionId",
      exact: true,
      strict: false,
    });

    subscriptionStore.subscriptionId = parseInt(match.params.subscriptionId, 10);
    subscriptionStore
      .load()
      .then(() => {
        const { subscription } = subscriptionStore;

        if (subscription.canceledDts) {
          redirectTo("/billing-central/subscriptions");
        }

        const { actualUser } = actualUserStore;

        creditCardStore.pagination.itemsPerPage = 100;
        creditCardStore.updateSearchParams({
          currency: actualUser.currency,
        });

        creditCardStore.loadIndex().catch(error => showError(error.message));

        this.bindForm(actualUser, subscription);

        taxStore.searchParams.state = actualUser.address.state;
        taxStore.searchParams.country = actualUser.address.country;

        taxStore.loadIndex().catch(error => showError(error.message));
      })
      .catch(error => showError(error.message));

    otherProductStore.loadIndex().catch(error => showError(error.message));
  }

  showConfirmationModal = () => this.setState({ confirmationModalVisible: true });

  hideConfirmationModal = () => this.setState({ confirmationModalVisible: false });

  showCancelAtPeriodEndConfirmationModal = () => this.setState({ cancelAtPeriodEndConfirmationModalVisible: true });

  hideCancelAtPeriodEndConfirmationModal = () => this.setState({ cancelAtPeriodEndConfirmationModalVisible: false });

  update() {
    const { t, toastMessageStore, showError, actualUserStore } = this.props;
    const { subscriptionStore, formStore, creditCardId } = this.subscriptionPageStore;
    const { model } = formStore;
    const { subscriptionItems } = model;
    const { actualUser } = actualUserStore;

    subscriptionStore
      .update({ creditCardId, subscriptionItems })
      .then(() => {
        const { subscription } = subscriptionStore;

        this.bindForm(actualUser, subscription);

        toastMessageStore.addToastMessage(t("billingCentral:subscriptionHasBeenUpdated"), "positive", t("success"));
      })
      .catch(error => showError(error.message));
  }

  cancelAtPeriodEnd() {
    const { t, toastMessageStore, showError, redirectTo } = this.props;
    const { subscriptionStore } = this.subscriptionPageStore;

    subscriptionStore
      .update({ cancelAtPeriodEnd: true })
      .then(() => {
        toastMessageStore.addToastMessage(t("billingCentral:subscriptionHasBeenCanceled"), "positive", t("success"));
        redirectTo("/billing-central/subscriptions");
      })
      .catch(error => showError(error.message));
  }

  bindForm(actualUser, subscription) {
    const initialModel = {
      creditCardId: actualUser.defaultCreditCardId !== null ? actualUser.defaultCreditCardId : "",
      subscriptionItems: [],
    };

    subscription.nextBillingCycleSubscriptionItems.forEach(subscriptionItem => {
      initialModel.subscriptionItems.push({
        otherProductId: subscriptionItem.otherProductId,
        quantity: subscriptionItem.quantity,
      });
    });

    this.subscriptionPageStore.formStore = new FormStore(initialModel);
  }

  render() {
    const { t, toastMessageStore, stripeApiPublishableKey, actualUserStore } = this.props;
    const { confirmationModalVisible, cancelAtPeriodEndConfirmationModalVisible } = this.state;
    const {
      otherProductStore,
      subscriptionStore,
      formStore,
      taxStore,
      areFormSubscriptionItemsDirty,
      formSubscriptionItemChanges,
    } = this.subscriptionPageStore;
    const { isLoading: subscriptionLoading, isSaving: subscriptionSaving, subscription } = subscriptionStore;
    const { isLoading: otherProductLoading, otherProducts } = otherProductStore;
    const { taxes: applicableTaxes, isLoading: taxLoading } = taxStore;
    const { creditCardStore } = this.creditCardFormStore;
    const { isLoading: creditCardLoading } = creditCardStore;
    const { isLoading: actualUserLoading, actualUser } = actualUserStore;
    const { model } = formStore;
    const { subscriptionItems } = model;

    if (
      typeof subscriptionItems === "undefined" ||
      subscriptionLoading ||
      actualUserLoading ||
      creditCardLoading ||
      taxLoading ||
      otherProductLoading
    ) {
      return <ContentLoader />;
    }

    const clickHere = t("clickHere").toLowerCase();

    return (
      <>
        <LoadingWrapper
          loading={subscriptionSaving}
          message={`${t("billingCentral:updatingYourSubscription")} ${t("waitAMomentPlease")}`}
        />

        <div className="side-page-outer-container">
          <Breadcrumb>
            <Breadcrumb.Section>
              <Link to="/billing-central">{t("billingCentral:billingCentral")}</Link>
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section>
              <Link to="/billing-central/subscriptions">{t("billingCentral:subscriptions")}</Link>
            </Breadcrumb.Section>
            <Breadcrumb.Divider />
            <Breadcrumb.Section active>
              {t("billingCentral:uiMeet3dSubscriptionId", { id: subscription.subscriptionId })}
            </Breadcrumb.Section>
          </Breadcrumb>

          <div className="side-page-header no-flex" style={{ marginBottom: "1rem" }}>
            <div className="side-page-header-grid-wrap">
              <div className="side-page-h1-wrap">
                <h1>{t("billingCentral:editYourSubscription")}</h1>
              </div>
            </div>
          </div>

          <Grid centered className="main-grid">
            <Grid.Column>
              <SubscriptionEditForm
                subscriptionStore={subscriptionStore}
                otherProductStore={otherProductStore}
                formStore={formStore}
                creditCardFormStore={this.creditCardFormStore}
                toastMessageStore={toastMessageStore}
                actualUserStore={actualUserStore}
                quantityOptions={this.subscriptionPageStore.getQuantityOptions()}
                subtotal={this.subscriptionPageStore.calculateSubtotal(subscriptionItems, otherProducts)}
                totalAmountWithTaxes={this.subscriptionPageStore.getTotalAmountWithTaxes(
                  subscriptionItems,
                  otherProducts,
                  applicableTaxes,
                )}
                applicableTaxes={applicableTaxes}
                areFormSubscriptionItemsDirty={areFormSubscriptionItemsDirty}
                onSubmitCallback={() => this.showConfirmationModal()}
                stripeApiPublishableKey={stripeApiPublishableKey}
              />
              {subscription.downgradedSubscriptionItems.map(downgradedSubscriptionItem => (
                <DowngradedSubscriptionItemMessage
                  key={downgradedSubscriptionItem.subscriptionItemId}
                  subscriptionItem={downgradedSubscriptionItem}
                  otherProduct={otherProducts.find(
                    otherProduct => otherProduct.otherProductId === downgradedSubscriptionItem.otherProductId,
                  )}
                  endOfCurrentBillingCycleDate={subscription.endOfCurrentBillingCycleDate}
                />
              ))}
              <p style={{ marginTop: "1rem" }}>
                {t("billingCentral:ifYouWantToCancel")},{" "}
                <a onClick={() => this.showCancelAtPeriodEndConfirmationModal()} style={{ cursor: "pointer" }}>
                  {clickHere}.
                </a>
              </p>
            </Grid.Column>
          </Grid>
        </div>
        <Modal open={confirmationModalVisible} onClose={() => this.hideConfirmationModal()}>
          <Modal.Header>{t("billingCentral:areYouSureYouWantToUpdateTheSubscription")}</Modal.Header>
          <Modal.Content>
            {formSubscriptionItemChanges.length === 0 && (
              <p>{t("billingCentral:updateSubscriptionWithNoChargeDescription")}</p>
            )}
            {formSubscriptionItemChanges.length > 0 && subscription.needsNewBillingCycle && (
              <p>{t("billingCentral:updateSubscriptionNeedsNewBillingCycleDescription")}</p>
            )}
            {formSubscriptionItemChanges.length > 0 && !subscription.needsNewBillingCycle && (
              <>
                {!actualUser.isUsingExternalInvoicingForSubscriptions &&
                  actualUser.defaultCreditCardId !== formStore.model.creditCardId && (
                    <Message>
                      <Message.Header>{t("billingCentral:creditCardNotTheDefaultOne")}</Message.Header>
                      <p>{t("billingCentral:creditCardNotTheDefaultOneDescription")}</p>
                    </Message>
                  )}
                <p>{t("billingCentral:updateSubscriptionWithChargeDescription")}</p>
                <SubscriptionTable
                  subscriptionItems={formSubscriptionItemChanges}
                  otherProducts={otherProducts}
                  applicableTaxes={applicableTaxes}
                  subtotal={this.subscriptionPageStore.calculateSubtotal(
                    formSubscriptionItemChanges,
                    otherProducts,
                    subscription.daysTillNextBillingCycle,
                  )}
                  totalAmountWithTaxes={this.subscriptionPageStore.getTotalAmountWithTaxes(
                    formSubscriptionItemChanges,
                    otherProducts,
                    applicableTaxes,
                    subscription.daysTillNextBillingCycle,
                  )}
                  calculatePriceForDaysCallback={(price, days) =>
                    this.subscriptionPageStore.calculatePriceForDays(price, days)
                  }
                  daysTillNextBillingCycle={subscription.daysTillNextBillingCycle}
                />
              </>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button secondary onClick={() => this.hideConfirmationModal()}>
              {t("cancel")}
            </Button>
            <Button
              primary
              onClick={() => {
                this.update();
                this.hideConfirmationModal();
              }}
            >
              {t("ok")}
            </Button>
          </Modal.Actions>
        </Modal>
        <SubscriptionCancelAtPeriodEndConfirmationModal
          selectedSubscriptionData={{
            subscriptionId: subscription.subscriptionId,
            cancelAtPeriodEnd: subscription.cancelAtPeriodEnd,
            endOfCurrentBillingCycleDate: subscription.endOfCurrentBillingCycleDate.format("MMMM Do YYYY"),
          }}
          cancelSubscriptionAtPeriodEndCallback={() => this.cancelAtPeriodEnd(subscription.subscriptionId)}
          hideModalCallback={() => this.hideCancelAtPeriodEndConfirmationModal()}
          visible={cancelAtPeriodEndConfirmationModalVisible}
        />
        <ToastMessageContainer toastMessageStore={toastMessageStore} />
      </>
    );
  }
}

SubscriptionEditPage.propTypes = {
  location: PropTypes.shape({
    pathname: PropTypes.string.isRequired,
  }),
  showError: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  toastMessageStore: PropTypes.instanceOf(ToastMessageStore),
  actualUserStore: PropTypes.oneOfType([PropTypes.instanceOf(ProviderStore), PropTypes.instanceOf(CustomerStore)]),
  stripeApiPublishableKey: PropTypes.string.isRequired,
};

export default withTranslation()(ErrorWrapper(RouteWrapper(SubscriptionEditPage)));
